import type { ComponentProps } from 'react'
import type { VariantProps } from 'class-variance-authority'

import Link from 'next/link'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-semibold whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
                outline: 'border-border bg-card shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary',
                ghost: 'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
                destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20',
                link: 'text-primary underline-offset-4 hover:underline',
                ai: 'bg-ai text-ai-foreground shadow-xs hover:bg-ai/90'
            },
            size: {
                default: 'h-9 gap-2 px-3.5',
                xs: 'h-7 gap-1 rounded-md px-2 text-xs [&_svg:not([class*=\'size-\'])]:size-3.5',
                sm: 'h-8 gap-1.5 rounded-md px-3 text-[0.8rem]',
                lg: 'h-11 gap-2 rounded-xl px-5 text-[0.95rem]',
                icon: 'size-9',
                'icon-xs': 'size-7 rounded-md [&_svg:not([class*=\'size-\'])]:size-3.5',
                'icon-sm': 'size-8 rounded-md',
                'icon-lg': 'size-11 rounded-xl'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

function Button({
    className,
    variant = 'default',
    size = 'default',
    ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
    return (
        <ButtonPrimitive
            data-slot='button'
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
}

/** A link that looks like a button. Use for navigation; use Button for actions. */
function ButtonLink({
    className,
    variant = 'default',
    size = 'default',
    ...props
}: ComponentProps<typeof Link> & VariantProps<typeof buttonVariants>) {
    return (
        <Link
            data-slot='button'
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
}

export { Button, ButtonLink, buttonVariants }
